import { useEffect, useRef, useState, useCallback } from "react";

/**
 * 149-cw-r154 — useDraftAutosave.
 *
 * Auto-save form state ke localStorage setiap N detik (default 5s), restore
 * otomatis saat form dibuka kembali. Guard "kalau tab tertutup mendadak,
 * data tidak hilang".
 *
 * Cara pakai:
 *   const initial = { date: today(), amount: "", ... };
 *   const [form, setForm] = useState(initial);
 *   const draft = useDraftAutosave({
 *     key: `catat-pengeluaran`,      // unique per form type/user context
 *     form,
 *     setForm,
 *     enabled: open,                  // hanya autosave saat dialog terbuka
 *     initialForm: initial,           // baseline utk deteksi dirty
 *   });
 *   // Setelah save sukses:
 *   draft.clear();
 *   // Kalau user mau abaikan draft:
 *   draft.dismiss();
 *
 * Return:
 *   - hasRestored (bool) — true kalau draft lama di-restore
 *   - restoredAt (number|null) — timestamp draft yang di-restore
 *   - clear() — hapus draft (dipakai setelah save sukses)
 *   - dismiss() — hapus draft + reset form ke initialForm (buang draft manual)
 */
const KEY_PREFIX = "ens_draft_v1:";

export function useDraftAutosave({
  key,
  form,
  setForm,
  enabled = true,
  initialForm = null,
  intervalMs = 5000,
}) {
  const [hasRestored, setHasRestored] = useState(false);
  const [restoredAt, setRestoredAt] = useState(null);
  const formRef = useRef(form);
  const initialRef = useRef(initialForm);
  const restoredKey = useRef(null);

  useEffect(() => { formRef.current = form; }, [form]);
  useEffect(() => { initialRef.current = initialForm; }, [initialForm]);

  const storageKey = `${KEY_PREFIX}${key}`;

  const isDirty = (cur, init) => {
    if (!init) return true;
    try { return JSON.stringify(cur) !== JSON.stringify(init); } catch { return true; }
  };

  // Restore dari localStorage saat enabled → true (dialog terbuka).
  // restoredKey ref cegah re-restore kalau enabled toggle bolak-balik dgn key yg sama.
  useEffect(() => {
    if (!enabled) { restoredKey.current = null; setHasRestored(false); setRestoredAt(null); return; }
    if (restoredKey.current === storageKey) return;
    restoredKey.current = storageKey;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.form && parsed?.ts) {
        setForm(parsed.form);
        setHasRestored(true);
        setRestoredAt(parsed.ts);
      }
    } catch { /* silent — corrupted draft tidak boleh crash form */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, storageKey]);

  // Interval autosave — hanya save kalau form dirty (beda dari initialForm).
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      const cur = formRef.current;
      const init = initialRef.current;
      if (!cur || !isDirty(cur, init)) return;
      try { localStorage.setItem(storageKey, JSON.stringify({ form: cur, ts: Date.now() })); } catch {}
    }, intervalMs);
    return () => clearInterval(id);
  }, [enabled, storageKey, intervalMs]);

  // Flush saat tab close (beforeunload) supaya data terakhir tidak hilang antara interval.
  useEffect(() => {
    if (!enabled) return;
    const flush = () => {
      const cur = formRef.current;
      const init = initialRef.current;
      if (!cur || !isDirty(cur, init)) return;
      try { localStorage.setItem(storageKey, JSON.stringify({ form: cur, ts: Date.now() })); } catch {}
    };
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush();
    });
    return () => window.removeEventListener("beforeunload", flush);
  }, [enabled, storageKey]);

  const clear = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch {}
    setHasRestored(false);
    setRestoredAt(null);
    restoredKey.current = null;
  }, [storageKey]);

  const dismiss = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch {}
    if (initialRef.current) setForm(initialRef.current);
    setHasRestored(false);
    setRestoredAt(null);
    restoredKey.current = null;
  }, [setForm, storageKey]);

  return { hasRestored, restoredAt, clear, dismiss };
}

/**
 * DraftRestoredBanner — komponen kecil siap pakai utk tampilkan info "draft dipulihkan".
 */
export function DraftRestoredBanner({ hasRestored, restoredAt, onDismiss, testid = "draft-restored-banner" }) {
  if (!hasRestored) return null;
  const dt = restoredAt ? new Date(restoredAt) : null;
  const label = dt
    ? dt.toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "sebelumnya";
  return (
    <div
      data-testid={testid}
      className="bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center justify-between gap-2 text-[11px] text-emerald-900"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-base leading-none">✓</span>
        <span className="truncate">
          <b>Draft dipulihkan</b> dari <span className="tabular-nums">{label}</span>. Silakan lanjutkan pengisian.
        </span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        data-testid={`${testid}-dismiss`}
        className="shrink-0 px-2 py-1 border border-emerald-500 bg-white text-emerald-800 hover:bg-emerald-100 text-[9px] uppercase tracking-wider font-semibold"
      >
        Buang Draft
      </button>
    </div>
  );
}
